/**
 * LotInheritanceBanner — V14.0
 * ============================
 * Sticky banner shown at the top of a Lot's "Fiche Lot" dialog whenever the
 * Unique ↔ Similaire relationship matters:
 *
 *  • Similaire  → "Hérite de S51-01" + [Détacher] button
 *  • Master with slaves → "Ce lot est référencé par 3 lots Similaires" + list
 *  • Autonomous Unique → renders nothing (no clutter)
 *
 * The banner does *not* duplicate the form field (LotCorrespondanceField
 * already handles editing). Its job is to make the inheritance state visible
 * at a glance and surface the irreversible "Détacher" action.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Unlink, Users, ArrowRightCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LotInheritanceBanner = ({ lotId, allLots, onLotUpdated }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!lotId) { setInfo(null); return; }
    try {
      const r = await axios.get(`${API}/lots/${lotId}/inheritance`, { withCredentials: true });
      setInfo(r.data || null);
    } catch {
      setInfo(null);
    }
  }, [lotId]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  if (!info) return null;

  // Case 1 — Similaire (this lot inherits from a master)
  if (info.is_inherited) {
    const masterId = info.master_lot_id;
    const onDetach = async () => {
      if (!window.confirm(
        `Détacher ce lot de ${info.master_lot_number} ?\n\n`
        + `Il deviendra Unique et pourra avoir ses propres fichiers et rendus.\n`
        + `Les fichiers/rendus du lot maître ne lui seront plus rattachés.`,
      )) return;
      setBusy(true);
      try {
        await axios.post(`${API}/lots/${lotId}/detach`, {}, { withCredentials: true });
        toast.success(`Lot détaché de ${info.master_lot_number} — désormais Unique`);
        await fetchInfo();
        onLotUpdated?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Échec du détachement');
      } finally {
        setBusy(false);
      }
    };
    return (
      <div
        className="rounded-md border-l-4 border-sky-400 bg-sky-50 p-3 flex items-start gap-3"
        data-testid="lot-inheritance-banner-similar"
      >
        <Link2 className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-sky-900">
            Hérite du lot {info.master_lot_number}
            <span className="ml-1.5 text-[10.5px] font-normal text-sky-700 uppercase tracking-wider">Similaire</span>
          </div>
          <p className="text-[11.5px] text-sky-800 mt-0.5">
            Ce lot partage les <strong>fichiers</strong> et les <strong>rendus</strong> du lot maître
            <strong> {info.master_lot_number} ({info.master_typology})</strong>. Seuls la localisation et l'étage diffèrent.
          </p>
        </div>
        <button
          type="button"
          onClick={onDetach}
          disabled={busy}
          className="shrink-0 h-7 px-2.5 inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider rounded border border-sky-300 bg-white text-sky-700 hover:bg-sky-100 disabled:opacity-50"
          data-testid="lot-inheritance-detach-btn"
          title="Convertir ce lot en Unique pour lui permettre d'avoir ses propres fichiers et rendus"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
          Détacher
        </button>
      </div>
    );
  }

  // Case 2 — Master (this lot has slaves)
  if (info.is_master && info.slave_count > 0) {
    return (
      <div
        className="rounded-md border-l-4 border-emerald-400 bg-emerald-50 p-3 flex items-start gap-3"
        data-testid="lot-inheritance-banner-master"
      >
        <Users className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-emerald-900">
            Lot maître · référencé par {info.slave_count} lot{info.slave_count > 1 ? 's' : ''} Similaire{info.slave_count > 1 ? 's' : ''}
          </div>
          <p className="text-[11.5px] text-emerald-800 mt-0.5">
            Les fichiers et rendus que tu ajoutes ici sont automatiquement partagés avec :{' '}
            <strong>{info.slaves.map((s) => s.lot_number || s.name).join(', ')}</strong>.
          </p>
        </div>
      </div>
    );
  }

  // Case 3 — Autonomous Unique. Render nothing to avoid noise.
  return null;
};

export default LotInheritanceBanner;
